import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { formatCZK, formatDate, formatTime, formatHours } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Check, Pencil } from "lucide-react";
import { toast } from "sonner";
import { ShiftReviewDialog } from "@/components/shared/ShiftReviewDialog";
import { ask } from "@/lib/confirm";

const fmtH = (h) => `${(h || 0).toFixed(1).replace(".", ",")} h`;
const isoWeek = (iso) => {
  const d = new Date(iso + "T00:00:00");
  const t = new Date(d); t.setHours(0, 0, 0, 0);
  t.setDate(t.getDate() + 3 - ((t.getDay() + 6) % 7));
  const w1 = new Date(t.getFullYear(), 0, 4);
  return `${t.getFullYear()}-${String(1 + Math.round(((t - w1) / 86400000 - 3 + ((w1.getDay() + 6) % 7)) / 7)).padStart(2, "0")}`;
};
const mondayISO = () => {
  const d = new Date(); d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

function Kpi({ label, value, sub, accent }) {
  return (
    <div className="bg-card border border-border rounded-[14px] p-4">
      <div className="text-[11px] font-bold uppercase tracking-[.1em] text-muted-foreground">{label}</div>
      <div className={`text-2xl font-extrabold tracking-tight tabular-nums mt-1 ${accent ? "text-brand" : ""}`}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

// readOnly (173): projektant vidí hodiny, ale nic neschvaluje ani neopravuje.
export function ProjectAttendanceTab({ projectId, readOnly = false }) {
  const [data, setData] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editRec, setEditRec] = useState(null);

  const load = useCallback(() => {
    api.get(`/attendance/overview?project_ids=${projectId}&group=day&page_size=500`).then((r) => setData(r.data)).catch(() => {});
    api.get(`/attendance/overview?project_ids=${projectId}&group=worker`).then((r) => setWorkers(r.data.rows || [])).catch(() => {});
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const records = useMemo(() => (data?.days || []).flatMap((d) => d.records), [data]);
  const pending = records.filter((r) => r.approval_status === "pending" && r.check_out);
  const weekFrom = mondayISO();
  const weekHours = records.filter((r) => r.date >= weekFrom).reduce((s, r) => s + (r.hours || 0), 0);
  const showCost = data?.summary?.total_cost != null;
  const pendingHours = pending.reduce((s, r) => s + (r.hours || 0), 0);
  const pendingCost = pending.reduce((s, r) => s + (r.cost || 0), 0);
  const suspicious = records.filter((r) => r.check_out && (r.hours || 0) < 5 / 60).length;

  const weeks = useMemo(() => {
    const agg = {};
    for (const r of records) { const w = isoWeek(r.date); agg[w] = (agg[w] || 0) + (r.hours || 0); }
    return Object.entries(agg).sort(([a], [b]) => a.localeCompare(b)).slice(-8)
      .map(([w, h]) => ({ w, label: w.slice(5), hours: h, current: w === isoWeek(weekFrom) }));
  }, [records, weekFrom]);
  const maxWeek = Math.max(1, ...weeks.map((x) => x.hours));
  const maxWorker = Math.max(1, ...workers.map((x) => x.hours || 0));

  const approve = async (sid) => {
    try { await api.post(`/attendance/${sid}/approve`, {}); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const approveAll = async () => {
    const ids = pending.map((r) => r.id);
    if (!ids.length) return;
    if (ids.length > 10 && !await ask(`Schválit ${ids.length} záznamů (${fmtH(pendingHours)}${showCost ? ` · ${formatCZK(pendingCost)}` : ""})?`)) return;
    setBusy(true);
    try { await api.post("/attendance/approve-bulk", { ids }); toast.success("Docházka schválena."); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="bg-card border border-border rounded-[14px] p-8 text-center text-muted-foreground">Načítám…</div>;

  const rowsSorted = [...records].sort((a, b) => {
    const pa = a.approval_status === "pending" ? 0 : 1;
    const pb = b.approval_status === "pending" ? 0 : 1;
    return pa - pb || String(b.date).localeCompare(String(a.date));
  });

  return (
    <div className="space-y-4" data-testid="project-attendance-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Odpracováno celkem" value={fmtH(data.summary.total_hours)} />
        {showCost && <Kpi label="Náklady na lidi" value={formatCZK(data.summary.total_cost)}
          sub={pending.length > 0 ? "vč. neschválených" : undefined} />}
        <Kpi label="Tento týden" value={fmtH(weekHours)} />
        <Kpi label="K odsouhlasení" value={pending.length} sub="záznamy" accent={pending.length > 0} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.55fr_1fr] gap-4 items-start">
        {/* Záznamy */}
        <div className="bg-card border border-border rounded-[14px] p-4 min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
            <div className="flex items-center gap-2">
              <h3 className="text-[15px] font-bold">Záznamy</h3>
              {pending.length > 0 && <span className="rounded-full bg-brand/15 text-brand px-2.5 py-0.5 text-xs font-bold">{pending.length} čekají</span>}
            </div>
            {!readOnly && pending.length > 0 && (
              <Button onClick={approveAll} disabled={busy} data-testid="proj-att-approve-all"
                className="h-[34px] rounded-[10px] bg-success hover:bg-success/90 text-white font-bold text-[13px]">
                <Check size={14} /> Schválit vše
              </Button>
            )}
          </div>

          {/* desktop table */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[11px] font-bold uppercase tracking-[.06em] text-muted-foreground">
                  <th className="pb-2">Člověk</th><th className="pb-2">Kdy</th>
                  <th className="pb-2 text-right">Hod.</th>
                  {showCost && <th className="pb-2 text-right">Náklad</th>}
                  <th className="pb-2 text-right">Stav</th>
                </tr>
              </thead>
              <tbody>
                {rowsSorted.map((r) => {
                  const p = r.approval_status === "pending";
                  return (
                    <tr key={r.id} className={`border-t border-border/60 ${p ? "" : "text-muted-foreground"}`} data-testid={`proj-att-rec-${r.id}`}>
                      <td className="py-2 font-semibold whitespace-nowrap">{r.user_name}</td>
                      <td className="py-2 whitespace-nowrap tabular-nums text-muted-foreground">
                        {formatDate(r.date)} · {formatTime(r.check_in)}–{r.check_out ? formatTime(r.check_out) : "…"}
                      </td>
                      <td className={`py-2 text-right font-bold tabular-nums ${(r.hours || 0) < 5 / 60 && r.check_out ? "text-destructive" : ""}`}>{fmtH(r.hours)}</td>
                      {showCost && <td className="py-2 text-right tabular-nums text-muted-foreground">{r.cost != null ? formatCZK(r.cost) : "—"}</td>}
                      <td className="py-2 text-right">
                        {!readOnly && p && r.check_out && (
                          <button onClick={() => setEditRec(r)} data-testid={`proj-att-edit-${r.id}`}
                            className="inline-flex items-center justify-center h-7 w-7 rounded-[8px] border border-border text-muted-foreground hover:text-foreground mr-1.5 align-middle" title="Opravit záznam">
                            <Pencil size={13} />
                          </button>
                        )}
                        {p && r.check_out && readOnly ? (
                          <span className="rounded-full bg-brand/15 text-brand px-2.5 py-0.5 text-xs font-bold">čeká</span>
                        ) : p && r.check_out ? (
                          <button onClick={() => approve(r.id)} data-testid={`proj-att-approve-${r.id}`}
                            className="inline-flex items-center justify-center h-7 w-7 rounded-[8px] bg-success text-white" title="Schválit"><Check size={14} /></button>
                        ) : r.check_out ? (
                          <span className="rounded-full bg-success/15 text-success px-2.5 py-0.5 text-xs font-bold">Schváleno</span>
                        ) : (
                          <span className="rounded-full bg-secondary text-muted-foreground px-2.5 py-0.5 text-xs font-bold">probíhá</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* mobile cards */}
          <div className="sm:hidden space-y-2">
            {rowsSorted.map((r) => {
              const p = r.approval_status === "pending";
              return (
                <div key={r.id} className={`rounded-[12px] border p-3 ${p ? "border-border" : "border-border/60 text-muted-foreground"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[15px] font-semibold">{r.user_name}</span>
                    <span className="text-[15px] font-bold tabular-nums">{fmtH(r.hours)}</span>
                  </div>
                  <div className="text-[13px] text-muted-foreground tabular-nums">{formatDate(r.date)} · {formatTime(r.check_in)}–{r.check_out ? formatTime(r.check_out) : "…"}</div>
                  {!readOnly && p && r.check_out && (
                    <div className="flex gap-2 mt-2">
                      <Button variant="outline" onClick={() => setEditRec(r)} className="flex-1 h-11 rounded-[10px] font-bold">Upravit</Button>
                      {p && <Button onClick={() => approve(r.id)} className="flex-1 h-11 rounded-[10px] bg-success hover:bg-success/90 text-white font-bold">Schválit</Button>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {records.length === 0 && <p className="text-sm text-muted-foreground">Na této zakázce zatím není žádná docházka.</p>}

          {(pending.length > 0 || suspicious > 0) && (
            <div className="flex items-center justify-between gap-3 flex-wrap border-t border-border/60 mt-3 pt-3 text-[13px]">
              <span className="text-muted-foreground">{suspicious > 0 ? `${suspicious} záznam pod 5 min — pravděpodobně omyl` : ""}</span>
              {pending.length > 0 && (
                <span>Čeká <b className="tabular-nums">{fmtH(pendingHours)}</b>{showCost ? <> · <span className="tabular-nums">{formatCZK(pendingCost)}</span></> : null}</span>
              )}
            </div>
          )}
        </div>

        {/* pravý sloupec */}
        <div className="space-y-4 min-w-0">
          <div className="bg-card border border-border rounded-[14px] p-4">
            <h3 className="text-[15px] font-bold mb-3">Hodiny po lidech</h3>
            {workers.slice(0, 6).map((w) => (
              <div key={w.user_id} className="mb-2.5">
                <div className="flex justify-between text-[13px]"><span className="font-semibold">{w.name}</span><span className="text-muted-foreground tabular-nums">{fmtH(w.hours)}</span></div>
                <div className="h-1.5 rounded-full bg-secondary overflow-hidden mt-1">
                  <div className="h-full bg-brand" style={{ width: `${((w.hours || 0) / maxWorker) * 100}%` }} />
                </div>
              </div>
            ))}
            {workers.length > 6 && <div className="text-xs text-muted-foreground">a další {workers.length - 6}</div>}
            {workers.length === 0 && <p className="text-sm text-muted-foreground">Zatím nikdo.</p>}
          </div>
          {weeks.length > 0 && (
            <div className="bg-card border border-border rounded-[14px] p-4">
              <h3 className="text-[15px] font-bold mb-3">Hodiny po týdnech</h3>
              <div className="flex items-end gap-2 h-[90px]">
                {weeks.map((x) => (
                  <div key={x.w} className="flex-1 flex flex-col items-center gap-1 min-w-0">
                    <div className={`w-full rounded-t-[6px] ${x.current ? "bg-brand" : "bg-secondary"}`}
                      style={{ height: `${Math.max(6, (x.hours / maxWeek) * 74)}px` }} title={fmtH(x.hours)} />
                    <span className={`text-[10px] tabular-nums ${x.current ? "font-bold" : "text-muted-foreground"}`}>{x.label}</span>
                  </div>
                ))}
              </div>
              <div className="text-xs text-muted-foreground mt-2 tabular-nums">Tento týden {fmtH(weekHours)}</div>
            </div>
          )}
        </div>
      </div>

      <ShiftReviewDialog sid={editRec?.id || null} mode="detail" canSeeMoney={showCost}
        onClose={() => setEditRec(null)} onChanged={load} />
    </div>
  );
}
